import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

export const CROPPER_TEMPLATE = `
  <cropper-canvas background>
    <cropper-image scalable translatable></cropper-image>
    <cropper-shade hidden></cropper-shade>
    <cropper-handle action="select" plain></cropper-handle>
    <cropper-selection aspect-ratio="1" initial-coverage="0.8" movable resizable>
      <cropper-grid role="grid" bordered covered></cropper-grid>
      <cropper-crosshair centered></cropper-crosshair>
      <cropper-handle action="move" theme-color="rgba(255, 255, 255, 0.35)"></cropper-handle>
      <cropper-handle action="n-resize"></cropper-handle>
      <cropper-handle action="e-resize"></cropper-handle>
      <cropper-handle action="s-resize"></cropper-handle>
      <cropper-handle action="w-resize"></cropper-handle>
      <cropper-handle action="ne-resize"></cropper-handle>
      <cropper-handle action="nw-resize"></cropper-handle>
      <cropper-handle action="se-resize"></cropper-handle>
      <cropper-handle action="sw-resize"></cropper-handle>
    </cropper-selection>
  </cropper-canvas>
`

export default class extends Controller {
  static targets = [
    "input",
    "fileName",
    "panel",
    "stage",
    "image",
    "status",
    "applyButton",
    "currentPreview",
    "croppedPreview",
    "croppedImage",
    "croppedFileName",
    "removeInput",
    "cachedInput",
  ]

  connect() {
    this.cropper = null
    this.objectUrl = null
    this.croppedObjectUrl = null
    this.sourceFile = null
    this.originalFileName = null
    this.cropTimeout = null
    this.cropVersion = 0
    this.currentRemoved = !this.removeInputTarget.disabled
    this.scheduleCrop = this.scheduleCrop.bind(this)
  }

  disconnect() {
    this.reset()
  }

  async load() {
    const file = this.inputTarget.files?.[0]
    if (!file) return this.reset()

    this.setFileName(file.name)
    if (!file.type.startsWith("image/")) {
      this.sourceFile = null
      this.closeEditor()
      this.setStatus("Vyberte obrázek.")
      return
    }

    this.sourceFile = file
    this.originalFileName = file.name || "foto.jpg"
    this.openEditor()
  }

  openEditor() {
    if (!this.sourceFile) return

    this.destroyCropper()
    this.revokeObjectUrl()
    const cropVersion = this.cropVersion
    this.objectUrl = URL.createObjectURL(this.sourceFile)
    this.imageTarget.onload = () => {
      if (cropVersion !== this.cropVersion) return

      this.panelTarget.hidden = false
      this.cropper = new Cropper(this.imageTarget, {
        container: this.stageTarget,
        template: CROPPER_TEMPLATE,
      })
      this.cropper.getCropperSelection()?.addEventListener("change", this.scheduleCrop)
      this.cropper.getCropperCanvas()?.addEventListener("actionend", this.scheduleCrop)
      this.setStatus("Vyberte čtvercový výřez. Obrázek bude uložen jako 1000 × 1000 px.")
      this.scheduleCrop()
    }
    this.imageTarget.src = this.objectUrl
  }

  editCropped(event) {
    event.preventDefault()
    this.openEditor()
  }

  async apply(event) {
    event?.preventDefault()
    clearTimeout(this.cropTimeout)
    this.setApplyState(true)
    this.setStatus("Ukládám ořez...")

    const saved = await this.writeCroppedFile()
    if (saved) this.closeEditor()
  }

  removeCurrent(event) {
    event.preventDefault()
    this.currentRemoved = true
    this.removeInputTarget.disabled = false
    if (this.hasCachedInputTarget) this.cachedInputTarget.disabled = true
    if (this.hasCurrentPreviewTarget) this.currentPreviewTarget.hidden = true
  }

  removeCropped(event) {
    event?.preventDefault()
    this.clearInput()
    this.sourceFile = null
    this.closeEditor()
    this.revokeCroppedObjectUrl()
    this.originalFileName = null
    this.croppedPreviewTarget.hidden = true
    this.croppedImageTarget.removeAttribute("src")
    this.croppedFileNameTarget.textContent = ""
    this.setFileName("Nebyl vybrán žádný soubor")
    this.setStatus("")
    if (this.hasCurrentPreviewTarget && !this.currentRemoved) this.currentPreviewTarget.hidden = false
  }

  reset() {
    this.sourceFile = null
    this.closeEditor()
    this.revokeCroppedObjectUrl()
    this.setFileName("Nebyl vybrán žádný soubor")
    this.originalFileName = null
    this.croppedPreviewTarget.hidden = true
  }

  closeEditor() {
    this.destroyCropper()
    this.revokeObjectUrl()
    this.panelTarget.hidden = true
    this.setStatus("")
  }

  scheduleCrop() {
    clearTimeout(this.cropTimeout)
    this.cropTimeout = setTimeout(() => this.writeCroppedFile(), 250)
  }

  async writeCroppedFile() {
    const selection = this.cropper?.getCropperSelection()
    if (!selection) {
      this.setApplyState(false)
      this.setStatus("Nejdříve vyberte obrázek.")
      return false
    }

    try {
      const cropVersion = ++this.cropVersion
      const output = await selection.$toCanvas({ width: 1000, height: 1000 })
      if (cropVersion !== this.cropVersion) return false

      const blob = await this.canvasBlob(output)
      if (cropVersion !== this.cropVersion) return false

      const fileName = this.croppedFileName()
      const croppedFile = new File([blob], fileName, { type: "image/jpeg" })
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(croppedFile)
      this.inputTarget.files = dataTransfer.files
      this.showCroppedPreview(blob, fileName)
      this.setApplyState(false)
      this.setStatus("Ořez použit. Příloha bude uložena jako 1000 × 1000 px.")
      return true
    } catch {
      this.setApplyState(false)
      this.setStatus("Ořez se nepodařilo uložit.")
      return false
    }
  }

  canvasBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Image conversion failed")), "image/jpeg", 0.92)
    })
  }

  croppedFileName() {
    return this.originalFileName.replace(/\.[^.]+$/, "") + "-1000x1000.jpg"
  }

  destroyCropper() {
    clearTimeout(this.cropTimeout)
    this.cropVersion += 1
    this.imageTarget.onload = null
    this.cropper?.getCropperSelection()?.removeEventListener("change", this.scheduleCrop)
    this.cropper?.getCropperCanvas()?.removeEventListener("actionend", this.scheduleCrop)
    this.cropper?.destroy()
    this.cropper = null
    this.imageTarget.removeAttribute("src")
  }

  revokeObjectUrl() {
    if (!this.objectUrl) return

    URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = null
  }

  revokeCroppedObjectUrl() {
    if (!this.croppedObjectUrl) return

    URL.revokeObjectURL(this.croppedObjectUrl)
    this.croppedObjectUrl = null
  }

  showCroppedPreview(blob, fileName) {
    this.revokeCroppedObjectUrl()
    this.croppedObjectUrl = URL.createObjectURL(blob)
    this.croppedImageTarget.src = this.croppedObjectUrl
    this.croppedImageTarget.alt = fileName
    this.croppedFileNameTarget.textContent = fileName
    this.croppedPreviewTarget.hidden = false
    if (this.hasCurrentPreviewTarget) this.currentPreviewTarget.hidden = true
  }

  clearInput() {
    this.inputTarget.files = new DataTransfer().files
  }

  setStatus(message) {
    this.statusTarget.textContent = message
  }

  setFileName(message) {
    this.fileNameTarget.textContent = message
  }

  setApplyState(applying) {
    if (!this.hasApplyButtonTarget) return

    this.applyButtonTarget.disabled = applying
    this.applyButtonTarget.textContent = applying ? "Ukládám ořez..." : "Použít ořez"
  }
}
